import { z } from 'zod';
/**
 * Phase 3c.1 — service profiles drive the public self-service booking
 * experience. One per "thing the customer offers to be booked for"
 * (e.g. "Sales Consultation"). The slug uniquely names the booking page
 * within a customer's account.
 */
export const BusinessHoursDaySchema = z.object({
    open: z.string().regex(/^\d{2}:\d{2}$/),
    close: z.string().regex(/^\d{2}:\d{2}$/),
});
export const BUSINESS_DAYS = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
];
export const BusinessHoursSchema = z.object({
    monday: BusinessHoursDaySchema.optional(),
    tuesday: BusinessHoursDaySchema.optional(),
    wednesday: BusinessHoursDaySchema.optional(),
    thursday: BusinessHoursDaySchema.optional(),
    friday: BusinessHoursDaySchema.optional(),
    saturday: BusinessHoursDaySchema.optional(),
    sunday: BusinessHoursDaySchema.optional(),
});
export const FormFieldSchema = z.object({
    /** stable identifier used as the JSON key in submitted bookings */
    key: z.string().regex(/^[a-z][a-z0-9_]*$/),
    label: z.string().min(1).max(120),
    type: z.enum(['text', 'textarea', 'select', 'checkbox']),
    required: z.boolean().default(false),
    options: z.array(z.string()).optional(),
    placeholder: z.string().optional(),
});
export const BrandingSchema = z.object({
    primary_color: z
        .string()
        .regex(/^#[0-9A-Fa-f]{6}$/)
        .optional(),
    logo_url: z.string().url().optional(),
    intro_text: z.string().max(2000).optional(),
});
export const ServiceProfileSchema = z.object({
    id: z.string().regex(/^sp_[A-Za-z0-9_-]+$/),
    customer_id: z.string().regex(/^cust_[A-Za-z0-9_-]+$/),
    slug: z
        .string()
        .min(1)
        .max(80)
        .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, {
        message: 'slug must be lowercase alphanumeric with optional dashes',
    }),
    name: z.string().min(1).max(200),
    slot_duration_minutes: z.number().int().min(5).max(8 * 60),
    business_hours: BusinessHoursSchema,
    blackout_dates: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)),
    form_fields: z.array(FormFieldSchema),
    branding: BrandingSchema,
    auto_confirm: z.boolean(),
    /** bcrypt hash; absent/null means the calendar is public */
    browse_password_hash: z.string().nullable(),
    redirect_url: z.string().url().nullable(),
    email_account_id: z.string().regex(/^ema_[A-Za-z0-9_-]+$/).nullable(),
    notification_emails: z.array(z.string().email()),
    status: z.enum(['active', 'disabled']),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const ServiceProfileCreateSchema = z.object({
    slug: ServiceProfileSchema.shape.slug,
    name: ServiceProfileSchema.shape.name,
    slot_duration_minutes: z.number().int().min(5).max(8 * 60).default(30),
    business_hours: BusinessHoursSchema.default({}),
    blackout_dates: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)).default([]),
    form_fields: z.array(FormFieldSchema).default([]),
    branding: BrandingSchema.default({}),
    auto_confirm: z.boolean().default(true),
    /** Plain-text password — server bcrypts before persisting. */
    browse_password: z.string().min(4).max(128).optional(),
    redirect_url: z.string().url().optional(),
    email_account_id: z.string().regex(/^ema_[A-Za-z0-9_-]+$/).optional(),
    notification_emails: z.array(z.string().email()).default([]),
});
export const ServiceProfileUpdateSchema = ServiceProfileCreateSchema.partial()
    .extend({ status: z.enum(['active', 'disabled']).optional() })
    .refine((v) => Object.keys(v).length > 0, { message: 'at least one field required' });
/**
 * Public-facing slim view of a service profile — what the embed widget
 * sees. Excludes the bcrypt hash, customer-internal email_account_id, and
 * notification recipients.
 */
export const ServiceProfilePublicSchema = z.object({
    slug: ServiceProfileSchema.shape.slug,
    name: ServiceProfileSchema.shape.name,
    slot_duration_minutes: ServiceProfileSchema.shape.slot_duration_minutes,
    form_fields: ServiceProfileSchema.shape.form_fields,
    branding: ServiceProfileSchema.shape.branding,
    /** True iff browse_password_hash is set; never returns the hash itself. */
    requires_browse_password: z.boolean(),
});
//# sourceMappingURL=service-profiles.js.map